import { type ReactNode, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Mail, MessageCircle, AlertTriangle } from "lucide-react";

interface SupportDialogProps {
  trigger?: ReactNode;
  /** Controlled mode: when provided, no trigger is rendered and open state is managed by the parent */
  open?: boolean;
  onClose?: () => void;
}

export function SupportDialog({ trigger, open: controlledOpen, onClose }: SupportDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const close = () => (isControlled ? onClose?.() : setInternalOpen(false));

  return (
    <>
      {isControlled ? null : trigger ? (
        <div onClick={() => setInternalOpen(true)}>{trigger}</div>
      ) : (
        <button
          onClick={() => setInternalOpen(true)}
          className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
        >
          Support
        </button>
      )}

      <Dialog open={open} onClose={close} className="relative z-50">
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl transition-all">
              <div className="absolute right-4 top-4">
                <button
                  onClick={close}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                <img
                  src="/OS_ICON.png"
                  alt="Logo"
                  className="w-5 h-5 object-contain"
                />
                Support & Help
              </DialogTitle>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                OSMOAIR is currently in beta. Some features might experience issues.
              </p>

              <div className="mt-5 space-y-4">
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Beta Version Notice
                    </span>
                  </div>
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    This is a beta version of OSMOAIR. You may encounter bugs or
                    unexpected behavior. We appreciate your patience as we work to
                    improve the platform.
                  </p>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Contact Support
                  </h4>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <Mail className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Email Support
                      </p>
                      <a
                        href="mailto:support@osmoair.com"
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        support@osmoair.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <MessageCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        WhatsApp Support
                      </p>
                      <a
                        href="https://wa.me/918050653783"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                      >
                        +91-8050653783
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
}
